import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { CalendarPlus, RotateCcw } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

// Format a UTC ISO instant into {date:'YYYY-MM-DD', time:'HH:MM'} in the given timezone.
const partsInTz = (iso, tz) => {
  try {
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
    const p = Object.fromEntries(fmt.formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
    const hour = p.hour === "24" ? "00" : p.hour;
    return { date: `${p.year}-${p.month}-${p.day}`, time: `${hour}:${p.minute}` };
  } catch {
    return { date: "", time: "" };
  }
};

const EMPTY = { client_name: "", date: "", start_time: "", duration_minutes: 50, client_email: "", title: "", private_note: "", client_email_lang: "en" };

// Client email languages — extend this list to add more without restructuring the flow.
const EMAIL_LANGS = [{ value: "es", label: "Español" }, { value: "en", label: "English" }];

export function AddAppointmentDialog({ open, onOpenChange, onDone, editAppt = null }) {
  const { t } = useLang();
  const isEdit = !!editAppt;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      const { date, time } = partsInTz(editAppt.start_utc, editAppt.psych_timezone);
      setForm({ ...EMPTY, date, start_time: time, duration_minutes: editAppt.minutes || 50,
        client_email: editAppt.client_email || "", client_email_lang: editAppt.client_email_lang || "en" });
    } else {
      setForm({ ...EMPTY });
    }
  }, [open, isEdit, editAppt]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!isEdit && !form.client_name.trim()) { toast.error(t("booking.enter_client_name")); return; }
    if (!form.date || !form.start_time) { toast.error(t("booking.choose_date_time")); return; }
    const dur = parseInt(form.duration_minutes, 10);
    if (!dur || dur <= 0) { toast.error(t("booking.invalid_duration")); return; }
    const email = form.client_email.trim();
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/appointments/${editAppt.id}/manual-reschedule`, {
          date: form.date, start_time: form.start_time, duration_minutes: dur,
          client_email: email || null,
          client_email_lang: email ? form.client_email_lang : null,
        });
        toast.success(t("booking.appt_rescheduled"));
      } else {
        await api.post("/appointments/manual", {
          client_name: form.client_name.trim(),
          date: form.date,
          start_time: form.start_time,
          duration_minutes: dur,
          client_email: email || null,
          title: form.title.trim() || null,
          private_note: form.private_note.trim() || null,
          client_email_lang: email ? form.client_email_lang : null,
        });
        toast.success(t("booking.appt_added"));
      }
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || t("booking.save_appt_err"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="add-appt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            {isEdit ? <RotateCcw className="h-5 w-5 text-terracotta" /> : <CalendarPlus className="h-5 w-5 text-terracotta" />}
            {isEdit ? t("booking.reschedule_appt") : t("booking.add_appt")}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? t("booking.choose_new_date_time")
              : t("booking.manual_desc")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!isEdit && (
            <div>
              <Label htmlFor="appt-client-name">{t("booking.client_name")} *</Label>
              <Input id="appt-client-name" data-testid="appt-client-name" value={form.client_name}
                onChange={(e) => set("client_name", e.target.value)} placeholder={t("booking.client_name_ph")} />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="appt-date">{t("booking.date")} *</Label>
              <Input id="appt-date" type="date" data-testid="appt-date" value={form.date} onChange={(e) => set("date", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="appt-time">{t("booking.start_time")} *</Label>
              <Input id="appt-time" type="time" data-testid="appt-start-time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} />
            </div>
          </div>

          <div>
            <Label htmlFor="appt-duration">{t("booking.duration_min")} *</Label>
            <Input id="appt-duration" type="number" min="5" step="5" data-testid="appt-duration"
              value={form.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} />
          </div>

          {!isEdit && (
            <>
              <div>
                <Label htmlFor="appt-title">{t("booking.type_title_opt")}</Label>
                <Input id="appt-title" data-testid="appt-title" value={form.title}
                  onChange={(e) => set("title", e.target.value)} placeholder={t("booking.type_title_ph")} />
              </div>

              <div>
                <Label htmlFor="appt-note">{t("booking.private_note_opt")}</Label>
                <Textarea id="appt-note" data-testid="appt-private-note" rows={2} value={form.private_note}
                  onChange={(e) => set("private_note", e.target.value)}
                  placeholder={t("booking.private_note_ph")} />
              </div>
            </>
          )}

          <div>
            <Label htmlFor="appt-email">{t("booking.client_email_opt")}</Label>
            <Input id="appt-email" type="email" data-testid="appt-client-email" value={form.client_email}
              onChange={(e) => set("client_email", e.target.value)} placeholder="client@example.com" />
            <p className="mt-1 text-xs text-muted-foreground">
              {form.client_email.trim()
                ? t("booking.email_will_send")
                : t("booking.email_internal")}
            </p>
          </div>

          {form.client_email.trim() && (
            <div data-testid="appt-email-lang-wrap">
              <Label>{t("booking.email_language")}</Label>
              <div className="mt-1 flex gap-2" data-testid="appt-email-lang-group">
                {EMAIL_LANGS.map((el) => (
                  <button type="button" key={el.value} onClick={() => set("client_email_lang", el.value)}
                    data-testid={`appt-email-lang-${el.value}`}
                    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${form.client_email_lang === el.value ? "border-terracotta bg-terracotta-light text-terracotta" : "border-border text-slateink/70 hover:border-terracotta/40"}`}>
                    {el.label}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{t("booking.email_lang_note")}</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="appt-cancel-btn">{t("common.cancel")}</Button>
            <Button onClick={submit} disabled={saving} data-testid="appt-save-btn">
              {saving ? t("common.saving") : (isEdit ? t("booking.save_changes") : t("booking.add_appt"))}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
